import { Group, Loader, Select, Stack, Text } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { IconBuildingStore, IconSitemap, IconWorld } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import type { ShareGrant, ShareKind, ShareTarget } from '@tnn/domain'
import { InitialsAvatar } from '@tnn/ui'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSession } from '../auth/session'
import classes from './sharing.module.css'

const kindIcons: Record<Exclude<ShareKind, 'user'>, typeof IconWorld> = {
  company: IconWorld,
  branch: IconBuildingStore,
  department: IconSitemap,
}

/** A grant's or target's face: a person's initials, else its kind's mark */
export function TargetAvatar({ target }: { target: Pick<ShareTarget, 'kind' | 'name'> }) {
  if (target.kind === 'user') return <InitialsAvatar name={target.name} size={36} />
  const Icon = kindIcons[target.kind]
  return (
    <span className={classes.mark} aria-hidden>
      <Icon size={20} />
    </span>
  )
}

export const targetKey = (target: Pick<ShareTarget, 'kind' | 'targetId'>) => `${target.kind}:${target.targetId}`

/** A target's name, the company's by its kind (`sharing.kinds`) */
export function useTargetName() {
  const { t } = useTranslation()
  return (target: Pick<ShareTarget, 'kind' | 'name'>) => (target.kind === 'company' ? t('sharing.kinds.company') : target.name)
}

export interface GrantsEditorProps<Role extends string> {
  /** The grants being edited (null: still loading) */
  grants: ShareGrant<Role>[] | null
  onChange: (grants: ShareGrant<Role>[]) => void
  roles: readonly Role[]
  roleLabel: (role: Role) => string
  /** What the role picker starts at for someone added */
  defaultRole: Role
  /** Whom it may be shared with, by what is typed (the app's `/targets`), cached under `queryKey` */
  search: (q: string) => Promise<ShareTarget[]>
  queryKey: readonly unknown[]
  /** Under the list: what each role may do */
  notes?: { intro?: string; roles: { role: Role; hint: string }[] }
}

/**
 * Who something of mine is shared with, edited in place (Drive's top folders, Boards' boards): add a person, branch,
 * department or the whole company as a role, change or take away anyone's; its owner (me) first. Saving is the
 * dialog's own
 */
export function GrantsEditor<Role extends string>({
  grants,
  onChange,
  roles,
  roleLabel,
  defaultRole,
  search: find,
  queryKey,
  notes,
}: GrantsEditorProps<Role>) {
  const { t } = useTranslation()
  const { me } = useSession()
  const nameOf = useTargetName()
  const [search, setSearch] = useState('')
  const [q] = useDebouncedValue(search, 250)
  const [role, setRole] = useState<Role>(defaultRole)
  const found = useQuery({ queryKey: [...queryKey, 'targets', q], queryFn: () => find(q) })
  const all = grants ?? []
  const taken = new Set(all.map(targetKey))
  const options = (found.data ?? [])
    .filter((target) => !taken.has(targetKey(target)))
    .map((target) => ({
      value: targetKey(target),
      label: `${nameOf(target)}${target.kind === 'company' ? '' : ` · ${t(`sharing.kinds.${target.kind}`)}`}${target.sub ? ` · ${target.sub}` : ''}`,
    }))
  const add = (value: string | null) => {
    const target = found.data?.find((x) => targetKey(x) === value)
    if (!target) return
    onChange([...all, { ...target, role }])
    setSearch('')
  }
  const change = (key: string, next: string | null) =>
    onChange(
      next === 'remove'
        ? all.filter((g) => targetKey(g) !== key)
        : all.map((g) => (targetKey(g) === key ? { ...g, role: next as Role } : g)),
    )
  const roleOptions: { value: string; label: string }[] = roles.map((r) => ({ value: r, label: roleLabel(r) }))

  return (
    <Stack gap="lg">
      <Group gap="xs" align="flex-end" wrap="nowrap">
        <Select
          className={classes.add}
          aria-label={t('sharing.add')}
          placeholder={t('sharing.add')}
          data={options}
          value={null}
          onChange={add}
          searchable
          searchValue={search}
          onSearchChange={setSearch}
          filter={({ options: shown }) => shown}
          nothingFoundMessage={found.isFetching ? '…' : t('form.noOptions')}
          rightSection={found.isFetching ? <Loader size="xs" /> : undefined}
        />
        <Select
          aria-label={t('sharing.role')}
          data={roleOptions}
          value={role}
          onChange={(v) => v && setRole(v as Role)}
          w={140}
          allowDeselect={false}
        />
      </Group>
      <Stack gap={4}>
        <Text fw={500}>{t('sharing.people')}</Text>
        <div className={classes.row}>
          <InitialsAvatar name={me.user.name} size={36} />
          <div className={classes.text}>
            <span className={classes.name}>{me.user.name}</span>
          </div>
          <Text size="sm" c="dimmed" pr="sm">
            {t('sharing.owner')}
          </Text>
        </div>
        {!grants ? (
          <Loader type="dots" />
        ) : (
          all.map((g) => (
            <div key={targetKey(g)} className={classes.row}>
              <TargetAvatar target={g} />
              <div className={classes.text}>
                <span className={classes.name}>{nameOf(g)}</span>
                <span className={classes.sub}>
                  {t(`sharing.kinds.${g.kind}`)}
                  {g.sub ? ` · ${g.sub}` : ''}
                </span>
              </div>
              <Select
                aria-label={t('sharing.role')}
                data={[...roleOptions, { value: 'remove', label: t('sharing.remove') }]}
                value={g.role}
                onChange={(v) => change(targetKey(g), v)}
                w={150}
                allowDeselect={false}
              />
            </div>
          ))
        )}
        {notes && (
          <div className={classes.notes}>
            {notes.intro && <span>{notes.intro}</span>}
            <ul>
              {notes.roles.map((r) => (
                <li key={r.role}>
                  <b>{roleLabel(r.role)}</b> {r.hint}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Stack>
    </Stack>
  )
}
